import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SocialPostStatus } from '@/lib/api';
import { socialMediaApi } from '../api';
import { socialMediaQueryKeys } from '../api/queryKeys';
import { toast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';

const PAGE_SIZE = 20;

/**
 * Lists posts for a Holder with pagination + status/search filters.
 * Stays idle while no Holder is selected; the page that owns this
 * hook is responsible for rendering the workspace-picker prompt.
 *
 * Offset pagination: each (status, search, page) combination is its own
 * cache entry. The previous page stays on screen during a page transition
 * (smooth, no empty-state flash) — but ONLY when just the page changed. A
 * Holder or filter switch drops the placeholder so one Holder's posts never
 * bleed into another's view.
 */
export function useSocialPosts(
  holderId: string | null,
  statusFilter?: SocialPostStatus,
  search?: string,
) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);

  // Reset to the first page whenever the Holder or filters change so we
  // never land on an out-of-range offset for a freshly-filtered list.
  useEffect(() => {
    setPage(0);
  }, [holderId, statusFilter, search]);

  const query = useQuery({
    queryKey: socialMediaQueryKeys.postsList(holderId ?? '', {
      status: statusFilter,
      search: search || undefined,
      page,
      limit: PAGE_SIZE,
    }),
    queryFn: () =>
      socialMediaApi.listSocialMediaPosts(holderId as string, {
        status: statusFilter,
        search: search || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
    enabled: !!holderId,
    // Keep the previous page's rows ONLY when just the page advanced (same
    // Holder + filters). On a Holder/filter switch the previous query's key
    // differs by more than page, so we drop the placeholder — no cross-Holder
    // flash.
    placeholderData: (previousData, previousQuery) => {
      if (!previousQuery) return undefined;
      const prevParams = previousQuery.queryKey[previousQuery.queryKey.length - 1] as {
        status?: string;
        search?: string;
      };
      const sameContext =
        previousQuery.queryKey.includes(holderId ?? '') &&
        prevParams?.status === statusFilter &&
        prevParams?.search === (search || undefined);
      return sameContext ? previousData : undefined;
    },
  });

  const posts = query.data?.posts ?? [];
  const total = query.data?.total ?? 0;

  const loadPosts = useCallback((targetPage: number) => {
    setPage(targetPage);
  }, []);

  const goToNextPage = useCallback(() => {
    setPage((p) => ((p + 1) * PAGE_SIZE < total ? p + 1 : p));
  }, [total]);

  const goToPrevPage = useCallback(() => {
    setPage((p) => (p > 0 ? p - 1 : p));
  }, []);

  const deleteMutation = useMutation({
    mutationFn: (postId: string) =>
      socialMediaApi.deleteSocialMediaPost(holderId as string, postId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: socialMediaQueryKeys.posts(holderId ?? ''),
      });
      toast({ title: 'Post deleted' });
      navigate('/a/arche_social_media');
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Failed to delete post';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    },
  });

  // mutateAsync is a stable reference (unlike the mutation result object), so
  // depending on it keeps deletePost stable across renders.
  const { mutateAsync: deletePostMutate } = deleteMutation;
  const deletePost = useCallback(
    async (postId: string) => {
      if (!holderId) return;
      // Errors are surfaced via the mutation's onError toast.
      await deletePostMutate(postId).catch(() => undefined);
    },
    [holderId, deletePostMutate],
  );

  const hasPrevPage = page > 0;
  const hasNextPage = (page + 1) * PAGE_SIZE < total;
  const firstItem = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const lastItem = Math.min((page + 1) * PAGE_SIZE, total);

  return {
    posts,
    total,
    isLoading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : null,
    loadPosts,
    page,
    hasPrevPage,
    hasNextPage,
    goToNextPage,
    goToPrevPage,
    firstItem,
    lastItem,
    deletePost,
  };
}
